import { Outlet, NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { useAuth } from "../../lib/auth";
import { Brand } from "../../components/Brand";
import api from "../../lib/api";

const NAV = [
    { to: "/customer", label: "Services", end: true, testid: "cust-nav-home", public: true },
    { to: "/customer/bookings", label: "My Bookings", testid: "cust-nav-bookings" },
    { to: "/customer/wallet", label: "Wallet", testid: "cust-nav-wallet" },
    { to: "/customer/membership", label: "Membership", testid: "cust-nav-mem" },
    { to: "/customer/refer", label: "Refer", testid: "cust-nav-refer" },
];

const MOBILE_NAV = [
    { to: "/customer", label: "Home", icon: "🏠", end: true, testid: "cust-nav-home", public: true },
    { to: "/customer/bookings", label: "Bookings", icon: "📅", testid: "cust-nav-bookings" },
    { to: "/customer/cart", label: "Cart", icon: "🛒", testid: "cust-nav-cart" },
    { to: "/customer/wallet", label: "Wallet", icon: "💳", testid: "cust-nav-wallet" },
    { to: "/customer/profile", label: "Profile", icon: "👤", testid: "cust-nav-profile" },
];

export default function CustomerLayout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [membership, setMembership] = useState(null);
    const [cartCount, setCartCount] = useState(0);
    const [showAddr, setShowAddr] = useState(false);
    const [addrIdx, setAddrIdx] = useState(0);
    const [showProfile, setShowProfile] = useState(false);
    const [search, setSearch] = useState("");
    const addrRef = useRef(null);
    const profRef = useRef(null);

    useEffect(() => {
        if (!user) {
            setMembership(null);
            setCartCount(0);
            return;
        }
        api.get("/membership/me").then(({ data }) => setMembership(data)).catch(() => {});
        api.get("/cart").then(({ data }) => setCartCount(data.item_count || 0)).catch(() => {});
    }, [loc.pathname, user]);

    useEffect(() => {
        const handler = (e) => {
            if (addrRef.current && !addrRef.current.contains(e.target)) setShowAddr(false);
            if (profRef.current && !profRef.current.contains(e.target)) setShowProfile(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const addresses = user?.addresses || [];
    const active = addresses[addrIdx];

    const submitSearch = (e) => {
        e.preventDefault();
        const q = search.trim();
        nav(q ? `/customer?q=${encodeURIComponent(q)}` : "/customer");
    };

    return (
        <div className="min-h-screen bg-white">
            <header className="sticky top-0 z-40 bg-white border-b border-[#E5E2DC]">
                <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-4 flex items-center gap-3 lg:gap-6">
                    <Link to="/customer" className="shrink-0">
                        <Brand size="small" />
                    </Link>

                    {/* Top nav pills (desktop) */}
                    <nav className="hidden lg:flex items-center gap-1 ml-2">
                        {NAV.filter((item) => user || item.public).map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                data-testid={item.testid}
                                className={({ isActive }) =>
                                    `px-3 py-2 text-sm font-medium tracking-wide transition relative ${
                                        isActive
                                            ? "text-[#180A21]"
                                            : "text-[#555] hover:text-[#180A21]"
                                    }`
                                }
                            >
                                {({ isActive }) => (
                                    <>
                                        {item.label}
                                        {isActive && (
                                            <span className="absolute left-3 right-3 -bottom-0.5 h-px bg-[#D4AF37]" />
                                        )}
                                    </>
                                )}
                            </NavLink>
                        ))}
                    </nav>

                    {/* Address selector — signed-in customers only */}
                    {user && (
                    <div className="relative hidden md:block" ref={addrRef}>
                        <button
                            data-testid="header-address-selector"
                            onClick={() => setShowAddr((v) => !v)}
                            className="flex items-center gap-2 px-4 py-2.5 border border-[#E5E2DC] hover:border-[#D4AF37] transition bg-white min-w-[200px] max-w-[260px]"
                        >
                            <svg className="w-4 h-4 text-[#D4AF37] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                            <div className="text-left min-w-0 flex-1">
                                <div className="text-[10px] uppercase tracking-wider text-[#B2912A]">
                                    {active?.label || "No address"}
                                </div>
                                <div className="text-sm text-[#180A21] truncate">
                                    {active?.line1 || "Add an address"}
                                </div>
                            </div>
                            <svg className="w-3 h-3 text-[#555]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/></svg>
                        </button>
                        {showAddr && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E5E2DC] shadow-xl z-50 min-w-[280px]">
                                {addresses.map((a, i) => (
                                    <button
                                        key={a.id}
                                        type="button"
                                        data-testid={`hdr-addr-${i}`}
                                        onClick={() => { setAddrIdx(i); setShowAddr(false); }}
                                        className={`block w-full text-left p-3 hover:bg-[#FAFAF8] border-b border-[#F3F2F0] last:border-0 ${i === addrIdx ? "bg-[#D4AF37]/5" : ""}`}
                                    >
                                        <div className="text-[10px] uppercase tracking-wider text-[#B2912A]">{a.label}</div>
                                        <div className="text-sm text-[#180A21]">{a.line1}</div>
                                        <div className="text-xs text-[#555]">{a.city}, {a.country}</div>
                                    </button>
                                ))}
                                <Link
                                    to="/customer/profile"
                                    onClick={() => setShowAddr(false)}
                                    data-testid="hdr-add-addr"
                                    className="block p-3 text-sm text-[#D4AF37] hover:bg-[#FAFAF8] border-t border-[#F3F2F0]"
                                >
                                    + Add new address
                                </Link>
                            </div>
                        )}
                    </div>
                    )}

                    {/* Search */}
                    <form onSubmit={submitSearch} className="flex-1 min-w-0 hidden sm:block">
                        <div className="relative">
                            <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                            <input
                                data-testid="header-search-input"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search for 'AC cleaning', 'plumbing', 'electrician'…"
                                className="w-full pl-11 pr-4 py-2.5 border border-[#E5E2DC] focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] bg-white text-sm"
                            />
                        </div>
                    </form>

                    {/* Cart shortcut with count badge — signed-in only */}
                    {user && (
                    <Link
                        to="/customer/cart"
                        data-testid="header-cart-btn"
                        className={`relative p-2.5 border border-[#E5E2DC] hover:border-[#D4AF37] transition ${loc.pathname.includes("/cart") ? "border-[#D4AF37]" : ""}`}
                        title="Cart"
                    >
                        <svg className="w-5 h-5 text-[#180A21]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293A1 1 0 005.414 17H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/></svg>
                        {cartCount > 0 && (
                            <span
                                data-testid="header-cart-count"
                                className="absolute -top-1.5 -right-1.5 bg-[#D4AF37] text-[#180A21] text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
                            >
                                {cartCount}
                            </span>
                        )}
                    </Link>
                    )}

                    {/* Profile dropdown (auth) or Sign in / Sign up (anon) */}
                    {user ? (
                    <div className="relative" ref={profRef}>
                        <button
                            data-testid="header-profile-btn"
                            onClick={() => setShowProfile((v) => !v)}
                            className="flex items-center gap-2 p-1 pr-3 border border-[#E5E2DC] hover:border-[#D4AF37] transition"
                        >
                            <div className="w-8 h-8 bg-[#180A21] text-[#D4AF37] flex items-center justify-center font-serif text-base">
                                {(user?.name || "U").charAt(0).toUpperCase()}
                            </div>
                            <div className="hidden sm:block text-left">
                                <div className="text-[10px] text-[#555] leading-none">Hello,</div>
                                <div className="text-xs text-[#180A21] font-medium leading-tight">{user?.name?.split(" ")[0]}</div>
                            </div>
                            {membership?.plan && (
                                <span
                                    data-testid="member-badge"
                                    className="hidden sm:inline-flex text-[9px] uppercase tracking-[0.15em] font-semibold text-[#D4AF37] border-l border-[#E5E2DC] pl-2 ml-1"
                                >
                                    ★ {membership.plan.tier}
                                </span>
                            )}
                        </button>
                        {showProfile && (
                            <div className="absolute top-full right-0 mt-1 bg-white border border-[#E5E2DC] shadow-xl z-50 w-56">
                                <div className="p-4 border-b border-[#F3F2F0]">
                                    <div className="text-sm font-medium text-[#180A21]">{user?.name}</div>
                                    <div className="text-xs text-[#555] truncate">{user?.email}</div>
                                </div>
                                {[
                                    ["/customer/profile", "Profile & addresses"],
                                    ["/customer/wallet", "Wallet"],
                                    ["/customer/membership", "Membership"],
                                    ["/customer/refer", "Refer & earn"],
                                    ["/customer/tickets", "Support tickets"],
                                ].map(([to, label]) => (
                                    <Link
                                        key={to}
                                        to={to}
                                        onClick={() => setShowProfile(false)}
                                        className="block px-4 py-2.5 text-sm text-[#180A21] hover:bg-[#FAFAF8] hover:text-[#D4AF37]"
                                    >
                                        {label}
                                    </Link>
                                ))}
                                <button
                                    data-testid="cust-logout-btn"
                                    onClick={async () => {
                                        await logout();
                                        nav("/login");
                                    }}
                                    className="block w-full text-left px-4 py-2.5 text-sm text-[#FF2D55] hover:bg-[#FAFAF8] border-t border-[#F3F2F0]"
                                >
                                    Sign out
                                </button>
                            </div>
                        )}
                    </div>
                    ) : (
                        <div className="flex items-center gap-2 shrink-0">
                            <Link
                                to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`}
                                data-testid="cust-nav-signin"
                                className="text-sm text-[#180A21] hover:text-[#D4AF37] transition px-3 py-2 font-medium"
                            >
                                Sign in
                            </Link>
                            <Link
                                to={`/register?next=${encodeURIComponent(loc.pathname + loc.search)}`}
                                data-testid="cust-nav-signup"
                                className="lx-btn-gold px-4 py-2 text-sm"
                            >
                                Sign up
                            </Link>
                        </div>
                    )}
                </div>
            </header>

            <main className="max-w-[1400px] mx-auto px-4 lg:px-8 py-8">
                <Outlet />
            </main>

            {/* mobile bottom nav */}
            <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#E5E2DC] flex z-30">
                {MOBILE_NAV.filter((item) => user || item.public).map((item) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        data-testid={`mob-${item.testid}`}
                        className={({ isActive }) =>
                            `flex-1 py-2.5 text-center text-[10px] font-medium transition ${
                                isActive ? "text-[#D4AF37]" : "text-[#555]"
                            }`
                        }
                    >
                        <div className="text-base">{item.icon}</div>
                        {item.label}
                    </NavLink>
                ))}
            </nav>
            <div className="lg:hidden h-14" />
        </div>
    );
}
